import HOC from "../../HOC";
import Logo from "../../../assets/icons/olinlogo.png";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { registerMpiRelation } from "../../../services/authApi";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import LoadingBackdrop from "../../../components/common/particles.jsx/LoadingBackdrop";
import ErrorPopup from "../../../components/common/particles.jsx/ErrorPopup";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const validationSchema = yup.object({
  email: yup.string().email().required("Email wajib diisi"),
  party_number: yup.string().required("Party number wajib diisi"),
  address: yup.string().required("Alamat wajib diisi"),
  password: yup.string().required().min(8, "Password minimal 8 karakter"),
  phone: yup.string().required("Nomor telepon wajib diisi"),
});

const RegisterMpi = () => {
  const navigate = useNavigate();

  const [confirmPassword, setConfirmPassword] = useState("");
  const [popupMessage, setPopupMessage] = useState("");
  const [showPassword, setShowPassword] = useState({
    password: false,
    confirmPassword: false,
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      party_number: "",
      address: "",
      password: "",
      phone: "",
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: (values) => {
      mutateRegister(values);
    },
  });

  const { mutate: mutateRegister, isLoading } = useMutation({
    mutationFn: registerMpiRelation,
    onSuccess: () => {
      setPopupMessage(
        "Akun berhasil dibuat! Anda akan diarahkan ke halaman login"
      );
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    },
    onError: (err) => {
      setPopupMessage(err.message?.id ? err.message.id : err.message);
    },
  });

  return (
    <div className="text-[12px] text-[#2C3E5D]">
      <LoadingBackdrop isLoading={isLoading} />
      <div className="md:flex items-stretch min-h-screen w-full">
        <div className="w-full basis-2/3 md:min-h-screen shadow-sm flex flex-col items-center justify-center p-2 pb-0 gap-4">
          <div className="flex items-center justify-center gap-4">
            <img
              src={Logo}
              className="w-[150px] h-[150px] lg:w-[200px] lg:h-[200px] object-contain"
            />
            <p className="font-bold text-[20px]">
              Registrasi Akun <br /> Relasi MPI
            </p>
          </div>
          <p className="text-left pb-4">
            Sudah memiliki akun OLIN?{" "}
            <a href="/login" className="text-blue-400 underline">
              Login
            </a>
          </p>
        </div>
        <div className="bg-gray-200 w-full min-h-screen flex items-start md:items-center justify-center p-4">
          <form onSubmit={formik.handleSubmit}>
            <div className="bg-white shadow-md rounded-lg p-4 max-h-[90vh] overflow-y-auto w-full">
              <p className="mb-2">
                Email <span className="text-red-500">*</span>
              </p>
              <TextField
                fullWidth
                sx={{ marginBottom: 2 }}
                placeholder="Email yang terdaftar"
                value={formik.values.email}
                onChange={(e) => formik.setFieldValue("email", e.target.value)}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />

              <p className="mb-2">
                Party Number <span className="text-red-500">*</span>
              </p>
              <TextField
                fullWidth
                sx={{ marginBottom: 2 }}
                placeholder="xxxxxxxxxxx"
                value={formik.values.party_number}
                onChange={(e) =>
                  formik.setFieldValue("party_number", e.target.value)
                }
                error={
                  formik.touched.party_number &&
                  Boolean(formik.errors.party_number)
                }
                helperText={
                  formik.touched.party_number && formik.errors.party_number
                }
              />

              <p className="mb-2">
                Nomor Telepon <span className="text-red-500">*</span>
              </p>
              <TextField
                fullWidth
                sx={{ marginBottom: 2 }}
                placeholder="081XXXXXXXX"
                value={formik.values.phone}
                onChange={(e) => {
                  if (!isNaN(Number(e.target.value)))
                    formik.setFieldValue("phone", e.target.value);
                }}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
              />

              <p className="mb-2">
                Alamat Lengkap <span className="text-red-500">*</span>
              </p>
              <TextField
                fullWidth
                sx={{ marginBottom: 2 }}
                placeholder="Alamat"
                value={formik.values.address}
                onChange={(e) =>
                  formik.setFieldValue("address", e.target.value)
                }
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
                multiline
                rows={4}
              />

              <p className="mb-2">
                Password <span className="text-red-500">*</span>
              </p>
              <TextField
                fullWidth
                sx={{ marginBottom: 2 }}
                type={showPassword.password ? "text" : "password"}
                autoComplete="false"
                placeholder="********"
                value={formik.values.password}
                onChange={(e) =>
                  formik.setFieldValue("password", e.target.value)
                }
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={formik.touched.password && formik.errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowPassword((prevVal) => ({
                            ...prevVal,
                            password: !prevVal.password,
                          }))
                        }
                      >
                        {showPassword.password ? (
                          <Visibility />
                        ) : (
                          <VisibilityOff />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <p className="mb-2">
                Konfirmasi Password <span className="text-red-500">*</span>
              </p>
              <TextField
                fullWidth
                sx={{ marginBottom: 2 }}
                type={showPassword.confirmPassword ? "text" : "password"}
                autoComplete="false"
                placeholder="********"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={
                  confirmPassword !== "" &&
                  confirmPassword !== formik.values.password
                }
                helperText={
                  confirmPassword !== "" &&
                  confirmPassword !== formik.values.password
                    ? "Password tidak sama"
                    : ""
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowPassword((prevVal) => ({
                            ...prevVal,
                            confirmPassword: !prevVal.confirmPassword,
                          }))
                        }
                      >
                        {showPassword.confirmPassword ? (
                          <Visibility />
                        ) : (
                          <VisibilityOff />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <button
                type="submit"
                className="py-2.5 rounded-md bg-blue-500 text-white hover:bg-gray-300 text-[16px] w-full disabled:bg-gray-300"
                disabled={
                  !formik.isValid ||
                  confirmPassword !== formik.values.password ||
                  formik.values.phone[0] !== "0" ||
                  isLoading
                }
              >
                Daftarkan Akun
              </button>
            </div>
          </form>
        </div>
      </div>

      <ErrorPopup
        errorMessage={popupMessage}
        setErrorMessage={setPopupMessage}
      />
    </div>
  );
};

export default HOC(RegisterMpi, "Login-required");
